import { firestore as db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';

// Available theme options
export type ThemeMode = 'light' | 'dark' | 'system';

export interface UserThemePreference {
  mode: ThemeMode;
  userId: string;
  updatedAt: Date;
}

// Interface for Firestore data
interface FirestoreUserThemePreference {
  mode: ThemeMode;
  userId: string;
  updatedAt: Timestamp;
}

const COLLECTION_NAME = 'user_preferences';

/**
 * Get the theme preference for a specific user
 */
export const getUserThemePreference = async (userId: string): Promise<UserThemePreference | null> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data() as FirestoreUserThemePreference;
      return {
        mode: data.mode,
        userId: data.userId,
        updatedAt: data.updatedAt.toDate()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user theme preference:', error);
    return null;
  }
};

/**
 * Set or update the theme preference for a specific user
 */
export const setUserThemePreference = async (userId: string, mode: ThemeMode): Promise<UserThemePreference> => {
  try {
    const now = new Date();
    const firestoreData = {
      userId,
      mode,
      updatedAt: Timestamp.fromDate(now)
    };
    
    const docRef = doc(db, COLLECTION_NAME, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      await updateDoc(docRef, firestoreData);
    } else {
      await setDoc(docRef, firestoreData);
    }
    
    return {
      userId,
      mode,
      updatedAt: now
    };
  } catch (error) {
    console.error('Error setting user theme preference:', error);
    throw error;
  }
};

/**
 * Get the default theme preference
 */
export const getDefaultThemePreference = (): ThemeMode => {
  return 'light'; // Default to light mode for admin panel
}; 